/**
 * Single-process React Flow canvas page.
 * Header (title / status / version) + canvas + simple side panel for the selected step.
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Workflow, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useProcess, useProcessGraph } from '../hooks';
import { ProcessCanvas } from '../components/ProcessCanvas';
import { StepPanel } from '../components/StepPanel';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary', active: 'default', deprecated: 'outline', archived: 'destructive',
};

export default function ProcessGraphPage() {
  const { processId } = useParams<{ processId: string }>();
  const navigate = useNavigate();
  const { data: process, isLoading } = useProcess(processId);
  const graphQuery = useProcessGraph(processId);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  if (isLoading || !processId) {
    return <div className="p-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>;
  }
  if (!process) {
    return (
      <div className="p-8">
        <p className="text-destructive">Process not found.</p>
        <Link to="/processes" className="text-primary hover:underline text-sm">← Back to processes</Link>
      </div>
    );
  }

  const selectedStep = graphQuery.data?.steps.find((s) => s.id === selectedStepId) || null;
  const selectedStepKpis = (graphQuery.data?.stepKpis || []).filter((k) => k.step_id === selectedStepId);
  const selectedStepSeats = (graphQuery.data?.stepSeats || []).filter((s) => s.step_id === selectedStepId);
  const processSeats = graphQuery.data?.processSeats || [];

  return (
    <div className="flex flex-col h-[calc(100vh-160px)]">
      <div className="flex items-center justify-between border-b pb-2 mb-2 px-4 pt-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/processes')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Workflow className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="font-semibold">{process.title}</div>
            <div className="text-xs text-muted-foreground font-mono">{process.slug}</div>
          </div>
          <Badge variant={STATUS_VARIANT[process.status] || 'secondary'}>{process.status}</Badge>
          <span className="text-xs text-muted-foreground tabular-nums">v{process.version}</span>
          <span className="text-xs text-muted-foreground">{process.category}</span>
        </div>
        <div className="flex items-center gap-2">
          {processSeats.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Owners:</span>
              {processSeats.slice(0, 3).map((s, i) => (
                <Badge key={i} variant="outline" className="text-[10px]">{s.seat_title} ({s.role})</Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-1 min-h-0">
        <ProcessCanvas processId={processId} onSelectStepId={setSelectedStepId} />
        <aside className="w-[320px] shrink-0 border-l overflow-y-auto p-4 bg-muted/20">
          {!selectedStep && (
            <div className="text-sm text-muted-foreground">
              Select a node on the canvas to edit its details, KPIs, and accountable seats.
            </div>
          )}
          {selectedStep && (
            <StepPanel
              processId={processId}
              step={selectedStep}
              stepKpis={selectedStepKpis}
              stepSeats={selectedStepSeats}
            />
          )}
        </aside>
      </div>
    </div>
  );
}
